// lib/schemaBuilder.ts
import { getTemplateFullImageUrl } from '@components/Modules/TemplatesModule/utils/urls';
import { LANGUAGES } from '@constants/languages';
import { BlogAuthorType } from '@lib/sanity/queries/blog/blogAuthor/types';
import { BlogCategoryType } from '@lib/sanity/queries/blog/blogCategory/types';
import { BlogPostPageType } from '@lib/sanity/queries/blog/blogPost/types';
import { BlogTagType } from '@lib/sanity/queries/blog/blogTag/types';
import { BentoType } from '@lib/sanity/queries/modules/bento/types';
import { CardsType } from '@lib/sanity/queries/modules/cards/types';
import { FAQType, QuestionProjection } from '@lib/sanity/queries/modules/faq/types';
import { FeaturesType } from '@lib/sanity/queries/modules/features/types';
import { BreadcrumbsType, HeroType } from '@lib/sanity/queries/modules/hero/types';
import { HeroOverlapType } from '@lib/sanity/queries/modules/heroOverlap/types';
import { RelatedResourcesType } from '@lib/sanity/queries/modules/relatedResources/types';
import { StepsType } from '@lib/sanity/queries/modules/steps/types';
import { TemplatesType } from '@lib/sanity/queries/modules/templates/types';
import { TestimonialsType } from '@lib/sanity/queries/modules/testimonials/types';
import { TestimonialsStatsType, TestimonialStatType } from '@lib/sanity/queries/modules/testimonialsStats/types';
import { FullPortableTextProjection, SanityI18nProjection } from '@lib/sanity/types/common';
import getProtableText from '@utils/getPortableTextText';
import { buildTemplateUrl, getBlogUrl, getDomain, getTemplatesUrl } from '@utils/urls';
import { PageData } from './AppLayout';

type Schema = Record<string, unknown>;

const SCHEMA_CONTEXT = 'https://schema.org';

type BuildSchemaProps = {
  data?: PageData;
  templates?: { title: string; url: string; imageUrl: string }[];
  posts?: { title: string; url: string; imageUrl: string }[];
  lang: string;
  websiteDomain: string;
  cdnUrl: string;
};

export function buildSchema({ data, templates, posts, lang, websiteDomain, cdnUrl }: BuildSchemaProps): Schema[] {
  const schemas: Schema[] = [];

  if (!data) {
    return [];
  }

  const isBlogPage = ['blogPage', 'blogPost', 'blogAuthor', 'blogCategory', 'blogTag'].includes(data._type);

  const language = getLanguage(lang);
  schemas.push(buildOrganizationSchema(websiteDomain));
  schemas.push(buildWebPageSchema(data, language, websiteDomain, isBlogPage));
  schemas.push(buildWebsiteSchema(language, websiteDomain, isBlogPage));
  // 1️⃣ PAGE-LEVEL SCHEMAS
  switch (data._type) {
    case 'blogPage':
      break;
    case 'blogPost': {
      const blogPage = data as BlogPostPageType;
      const blogBreadcrumbs = createBlogBreadcrumbs(blogPage, websiteDomain);

      schemas.push(buildArticleWebPageSchema(blogPage, language, websiteDomain));
      schemas.push(buildImageObjectSchema(blogPage, language, websiteDomain));
      schemas.push(
        buildBreadcrumbsSchema(
          blogBreadcrumbs,
          `${getBlogUrl(websiteDomain)}/${blogPage.slug}#breadcrumb`,
          websiteDomain,
        ),
      );

      schemas.push(buildPersonSchema(data.author, websiteDomain));
      schemas.push(buildNewsArticleWebPageSchema(blogPage, language, websiteDomain));
      addFAQEdgeCases(schemas, blogPage);
      break;
    }
    case 'blogAuthor':
      schemas.push(buildPersonSchema(data.author, websiteDomain));
      schemas.push(buildProfilePageSchema(data as BlogAuthorType, websiteDomain));
      break;
    case 'blogCategory':
      schemas.push(buildCollectionPageSchema(data as BlogCategoryType, language, websiteDomain));
      break;
    case 'blogTag':
      schemas.push(buildCollectionPageSchema(data as BlogTagType, language, websiteDomain));

      break;
    default:
      break;
  }

  if ('modules' in data && data.modules) {
    // 2️⃣ MODULE-LEVEL SCHEMAS
    data.modules?.forEach((module) => {
      switch (module._type) {
        case 'hero':
        case 'heroOverlap':
          addToSchemaHeroBreadcrumbs(schemas, module as HeroType | HeroOverlapType, websiteDomain);
          break;
        case 'faq':
          schemas.push(buildFAQSchema(module as FAQType));
          break;
        case 'features':
          schemas.push(buildFeaturesItemListSchema(module as FeaturesType));
          break;
        case 'bento':
          schemas.push(buildBentoItemListSchema(module as BentoType));
          break;
        case 'cards':
          schemas.push(buildCardsItemListSchema(module as CardsType));
          break;
        case 'steps':
          schemas.push(buildHowToSchema(module as StepsType));
          break;
        case 'testimonials':
          schemas.push(...buildReviewsSchema(module as TestimonialsType, websiteDomain));
          break;
        case 'testimonialsStats':
          schemas.push(...buildReviewsSchema(module as TestimonialsStatsType, websiteDomain));
          break;
        case 'templates':
          schemas.push(
            ...buildTemplatesSchema(
              (module as TemplatesType).templates?.map((template: NonNullable<TemplatesType['templates']>[number]) => ({
                title: template.alt ?? '',
                url: template.hash ? buildTemplateUrl(template.hash) : '',
                imageUrl: template.hash ? getTemplateFullImageUrl(template.hash, cdnUrl) : '',
              })),
              websiteDomain,
            ),
          );
          break;
        case 'relatedResources':
          schemas.push(...buildRelatedResourcesSchema(module as RelatedResourcesType, websiteDomain));
          break;
      }
    });
  }

  if (templates) {
    const isDetailPage = data?._type === 'templatePreview';
    schemas.push(...buildTemplatesSchema(templates, websiteDomain, isDetailPage));
  }

  if (posts) {
    const isBlogHome = data?._type === 'blogPage';
    schemas.push(...buildPostsSchema(posts, websiteDomain, isBlogHome));
  }

  return schemas;
}

//
// ---- PAGE SCHEMAS ----
//
function buildWebsiteSchema(lang: string, websiteDomain: string, isBlog = false): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@id': `${isBlog ? getBlogUrl(websiteDomain) : getDomain(websiteDomain)}#website`,
    '@type': 'WebSite',
    name: isBlog ? 'The Brief AI Blog' : 'The Brief AI',
    url: isBlog ? getBlogUrl(websiteDomain) : getDomain(websiteDomain),
    description: isBlog
      ? 'The Brief blog keeps you in The Loop on AI marketing & advertising with creative automation insights to discover, create, launch, and optimize your ads.'
      : 'The Brief is an AI agency that helps marketing teams discover, create, launch, and optimize advertising campaigns in one continuous loop.',

    publisher: {
      '@id': `${isBlog ? getBlogUrl(websiteDomain) : getDomain(websiteDomain)}#organization`,
    },
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: isBlog
            ? `${getBlogUrl(websiteDomain)}/?s={search_term_string}`
            : `${getDomain(websiteDomain)}/search?q={search_term_string}`,
        },
        'query-input': {
          '@type': 'PropertyValueSpecification',
          valueRequired: true,
          valueName: 'search_term_string',
        },
      },
    ],
    inLanguage: lang,
  };
}

function buildOrganizationSchema(websiteDomain: string): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@id': `${getDomain(websiteDomain)}#organization`,
    '@type': 'Organization',
    name: 'The Brief AI',
    alternateName: 'The Brief',
    url: getDomain(websiteDomain),
    logo: {
      '@type': 'ImageObject',
      '@id': `${getDomain(websiteDomain)}#logo`,
      url: `${getDomain(websiteDomain)}/favicon.ico`,
      width: 244,
      height: 238,
      caption: 'The Brief AI',
    },
    image: { '@id': `${getDomain(websiteDomain)}#logo` },
    sameAs: [
      'https://www.creatopy.com/#organization',
      'https://www.creatopy.com/',
      'https://www.linkedin.com/company/thebriefai/',
      'https://x.com/the_brief_ai/',
      'https://www.facebook.com/thebriefai',
      'https://www.instagram.com/thebrief.ai',
      'https://www.youtube.com/channel/UC49wCDKjcxy9--qt24rtSVQ',
      'https://www.pinterest.com/thebriefai',
      'https://www.tiktok.com/@thebriefai',
      // 'https://www.trustpilot.com/review/www.thebrief.ai',
      'https://www.crunchbase.com/organization/thebrief',
      'https://www.g2.com/products/the-brief',
      'https://www.producthunt.com/products/the-brief-2',
    ],
    foundingDate: '2025-10-01',
    foundingLocation: {
      '@type': 'Place',
      name: 'San Francisco, CA, USA',
    },
    employee: [
      {
        '@type': 'Person',
        '@id': `${getDomain(websiteDomain)}#person-ceo`,
        name: 'Tammy H. Nam',
        jobTitle: 'Chief Executive Officer (CEO)',
        sameAs: ['https://www.linkedin.com/in/tammynam'],
      },
      {
        '@type': 'Person',
        '@id': `${getDomain(websiteDomain)}#person-cpo`,
        name: 'Thibault Imbert',
        jobTitle: 'Chief Product Officer (CPO)',
        sameAs: ['https://www.linkedin.com/in/thibaultimbert', 'https://twitter.com/thibault_imbert'],
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@thebrief.ai',
        areaServed: 'Worldwide',
        availableLanguage: ['en'],
      },
    ],
  };
}

function buildWebPageSchema(page: PageData, lang: string, websiteDomain: string, isBlog?: boolean): Schema {
  function getUrl(page: Pick<PageData, 'seo' | 'slug' | '_type'>) {
    let url: string;
    switch (page._type) {
      case 'templateHome':
        url = getTemplatesUrl(websiteDomain);
        break;
      case 'templateSection':
      case 'templateDesignType':
        url = `${getTemplatesUrl(websiteDomain)}/${page.slug}`;
        break;
      case 'templatePreview':
        url = `${getTemplatesUrl(websiteDomain)}/t/${page.slug}`;
        break;
      case 'templateSinglePage':
        url = `${getTemplatesUrl(websiteDomain)}/s/${page.slug}`;
        break;
      case 'blogPage':
        url = getBlogUrl(websiteDomain);
        break;
      case 'blogPost':
        url = `${getBlogUrl(websiteDomain)}/${page.slug}`;
        break;
      case 'blogCategory':
        url = `${getBlogUrl(websiteDomain)}/category/${page.slug}`;
        break;
      case 'blogTag':
        url = `${getBlogUrl(websiteDomain)}/tag/${page.slug}`;
        break;
      case 'blogAuthor':
        url = `${getBlogUrl(websiteDomain)}/author/${page.slug}`;
        break;
      default:
        url = page.slug ? `${getDomain(websiteDomain)}/${page.slug}` : getDomain(websiteDomain);
    }
    // Ensure trailing slash for all URLs
    return url.endsWith('/') ? url : `${url}/`;
  }

  const baseSchema: Schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'WebPage',
    '@id': getUrl(page),
    name: page.seo?.title?.value,
    description: page.seo?.description?.value,
    isPartOf: {
      '@id': `${isBlog ? getBlogUrl(websiteDomain) : getDomain(websiteDomain)}#website`,
    },
    url: getUrl(page),
    inLanguage: lang,
    datePublished: formatDate(page?._createdAt),
    dateModified: formatDate(page?._updatedAt || page?._createdAt),
  };

  // Add blog-specific fields for blog detail pages
  if (page._type === 'blogPost') {
    const blogPage = page as BlogPostPageType;
    return {
      ...baseSchema,
      primaryImageOfPage: {
        '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#primaryimage`,
      },
      image: {
        '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#primaryimage`,
      },
      thumbnailUrl: blogPage.mainImage?.asset?.url,
      breadcrumb: {
        '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#breadcrumb`,
      },
      potentialAction: [
        {
          '@type': 'ReadAction',
          target: [getUrl(page)],
        },
      ],
    };
  }

  return baseSchema;
}

function buildNewsArticleWebPageSchema(page: BlogPostPageType, lang: string, websiteDomain: string): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${getBlogUrl(websiteDomain)}/${page.slug}`,
    },
    headline: page.seo?.title?.value,
    image: [page.mainImage?.asset?.url],
    author: { '@type': 'Person', name: page.author?.name },
    publisher: {
      '@type': 'Organization',
      name: 'The Brief AI Blog',
      logo: {
        '@type': 'ImageObject',
        url: `${getDomain(websiteDomain)}/favicon.ico`,
      },
    },
    datePublished: formatDate(page.publishedAt || page?._createdAt),
    dateModified: formatDate(page?._createdAt),
    inLanguage: lang,
  };
}

function buildArticleWebPageSchema(page: BlogPostPageType, lang: string, websiteDomain: string): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Article',
    '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#article`,
    isPartOf: {
      '@id': `${getBlogUrl(websiteDomain)}/${page.slug}`,
    },
    author: {
      name: page.author?.name,
      '@id': `${getBlogUrl(websiteDomain)}#person-${page.author?.slug}`,
    },
    headline: page.seo?.title?.value,
    datePublished: formatDate(page.publishedAt || page?._createdAt),
    dateModified: formatDate(page?._updatedAt || page?._createdAt) || formatDate(page?._createdAt),
    mainEntityOfPage: {
      '@id': `${getBlogUrl(websiteDomain)}/${page.slug}`,
    },
    wordCount: countWordsInPortableText(page.body?.value),
    publisher: {
      '@id': `${getDomain(websiteDomain)}#organization`,
    },
    image: {
      '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#primaryimage`,
    },
    thumbnailUrl: page.mainImage?.asset?.url,
    keywords: page.tags?.map((tag) => tag.title?.value),
    articleSection: page.categories?.map((category) => category.title?.value),
    inLanguage: lang,
  };
}

function buildCollectionPageSchema(
  page: {
    slug?: BlogCategoryType['slug'] | BlogTagType['slug'] | null;
    seo?: BlogCategoryType['seo'] | BlogTagType['seo'] | null;
  },
  lang: string,
  websiteDomain: string,
): Schema {
  return {
    '@type': 'CollectionPage',
    '@id': `${getBlogUrl(websiteDomain)}/category/${page.slug}#collection`,
    url: `${getBlogUrl(websiteDomain)}/category/${page.slug}`,
    name: page.seo?.title?.value,
    isPartOf: {
      '@id': `${getBlogUrl(websiteDomain)}/category/${page.slug}#website`,
    },
    primaryImageOfPage: {
      '@id': `${getBlogUrl(websiteDomain)}/category/${page.slug}#primaryimage`,
    },
    image: {
      '@id': `${getBlogUrl(websiteDomain)}/category/${page.slug}#primaryimage`,
    },
    thumbnailUrl: page.seo?.image?.asset?.url,
    description: page.seo?.description?.value,
    breadcrumb: {
      '@id': `${getBlogUrl(websiteDomain)}/category/${page.slug}#breadcrumb`,
    },
    inLanguage: lang,
  };
}

function buildProfilePageSchema(author: BlogAuthorType, websiteDomain: string) {
  return {
    '@type': 'ProfilePage',
    '@id': `${getBlogUrl(websiteDomain)}/author/${author.slug}#profile`,
    url: `${getBlogUrl(websiteDomain)}/author/${author.slug}`,
    name: `${author.name}, Author at The Brief AI Blog`,
    isPartOf: {
      '@id': `${getBlogUrl(websiteDomain)}#website`,
    },
    primaryImageOfPage: {
      '@id': `${getBlogUrl(websiteDomain)}/author/${author.slug}#primaryimage`,
    },
    image: {
      '@id': `${getBlogUrl(websiteDomain)}/author/${author.slug}#primaryimage`,
    },
    thumbnailUrl: author.avatar?.asset?.url,
    description: `Read all the articles by ${author.name} , author at The Brief AI Blog. Follow us on social media to see the latest articles .`,
    breadcrumb: {
      '@id': `${getBlogUrl(websiteDomain)}/author/${author.slug}#breadcrumb`,
    },
    inLanguage: 'en-US',
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [`${getBlogUrl(websiteDomain)}/author/${author.slug}`],
      },
    ],
  };
}

function buildImageObjectSchema(page: BlogPostPageType, lang: string, websiteDomain: string): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ImageObject',
    '@id': `${getBlogUrl(websiteDomain)}/${page.slug}#primaryimage`,
    inLanguage: lang,
    url: page.mainImage?.asset?.url,
    contentUrl: page.mainImage?.asset?.url,
    width: page.mainImage?.asset?.metadata?.dimensions?.width,
    height: page.mainImage?.asset?.metadata?.dimensions?.height,
  };
}

function buildPersonSchema(person: PageData['author'], websiteDomain: string): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Person',
    '@id': `${getDomain(websiteDomain)}#person-${person?.slug}`,
    name: person?.name,
    description: person?.bio,
    image: {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': `${getDomain(websiteDomain)}#person-image-${person?.slug}`,
      url: person?.avatar?.asset?.url,
      contentUrl: person?.avatar?.asset?.url,
      caption: person?.name,
    },
    url: `${getBlogUrl(websiteDomain)}/author/${person?.slug}`,
  };
}
//
// ---- MODULE SCHEMAS ----
//
function buildFAQSchema(module: { questions?: QuestionProjection[] }): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'FAQPage',
    mainEntity: module.questions?.map((faq) => ({
      '@type': 'Question',
      name: faq.question?.value,
      acceptedAnswer: {
        '@type': 'Answer',
        text: getProtableText(faq.answerRich) ?? faq.answer?.value,
      },
    })),
  };
}

function buildFeaturesItemListSchema(module: FeaturesType): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    itemListElement: module.features?.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title?.value,
    })),
  };
}

function buildBentoItemListSchema(module: BentoType): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    itemListElement: module.cells?.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: getProtableText(item.title),
    })),
  };
}

function buildCardsItemListSchema(module: CardsType): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    itemListElement: module.cells?.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title?.value,
    })),
  };
}

function buildHowToSchema(module: StepsType): Schema {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'HowTo',
    name: getProtableText(module.title),
    step: module.contentList?.map((step) => ({
      '@type': 'HowToStep',
      name: step.title?.value,
      text: getProtableText(step.description),
    })),
  };
}

type TestimonialItem = NonNullable<TestimonialsType['testimonials']>[number];

function buildReviewsSchema(module: TestimonialsType | TestimonialsStatsType, websiteDomain: string): Schema[] {
  return (
    module.testimonials
      // schema.org Review requires a named author; an entry without one is
      // invalid and would be flagged as "Missing field 'name' (in 'author')".
      ?.filter((review: TestimonialItem | TestimonialStatType) => review.name?.value)
      .map((review: TestimonialItem | TestimonialStatType) => ({
        '@context': SCHEMA_CONTEXT,
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: review.name?.value,
          ...(review.company?.text && review.company?.url
            ? {
                affiliation: {
                  '@type': 'Organization',
                  name: review.company?.text,
                  url: review.company?.url,
                },
              }
            : {}),
        },

        reviewBody: getProtableText(review.quote),
        itemReviewed: {
          '@type': 'Organization',
          name: 'The Brief AI',
          url: getDomain(websiteDomain),
        },
      })) || []
  );
}

function buildRelatedResourcesSchema(
  module: SanityI18nProjection<RelatedResourcesType>,
  websiteDomain: string,
): Schema[] {
  const res: Schema[] = [];
  if (!module.relatedResourceItems || module.relatedResourceItems.length === 0) {
    return res;
  }
  const items = [...module.relatedResourceItems]; // Clone to avoid mutation
  if (items[0]) {
    res.push({
      '@context': SCHEMA_CONTEXT,
      '@type': 'CreativeWork',
      name: items[0].title,
      url: `${getDomain(websiteDomain)}/${items[0].buttonLink}`,
      image: items[0].visuals?.default?.image?.image?.asset?.url,
    });
    items.splice(0, 1);
  }
  res.push({
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name: 'Related Resources',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      url: `${getDomain(websiteDomain)}/${item.buttonLink}`,
      image: item.visuals?.default?.image?.image?.asset?.url,
    })),
  });
  return res;
}

function buildTemplatesSchema(
  templates: { title: string; url: string; imageUrl: string }[] | undefined,
  websiteDomain: string,
  isDetailPage = false,
): Schema[] {
  const res: Schema[] = [];
  if (!templates || templates.length === 0) {
    return res;
  }
  if (isDetailPage && templates[0]) {
    res.push({
      '@context': SCHEMA_CONTEXT,
      '@type': 'CreativeWork',
      name: templates[0].title,
      url: `${getDomain(websiteDomain)}/${templates[0].url}`,
      image: templates[0].imageUrl,
    });
    templates.splice(0, 1);
  }
  res.push({
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name: 'Templates',
    itemListElement: templates.map((template, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: template.title,
      url: `${getDomain(websiteDomain)}/${template.url}`,
      image: template.imageUrl,
    })),
  });
  return res;
}

function buildPostsSchema(
  posts: { title: string; url: string; imageUrl: string }[],
  websiteDomain: string,
  isBlogHome: boolean,
): Schema[] {
  const res: Schema[] = [];
  if (!posts || posts.length === 0) {
    return res;
  }
  if (isBlogHome && posts[0]) {
    res.push({
      '@context': SCHEMA_CONTEXT,
      '@type': 'CreativeWork',
      name: posts[0].title,
      url: `${getDomain(websiteDomain)}/${posts[0].url}`,
      image: posts[0].imageUrl,
    });
    posts.splice(0, 1);
  }
  res.push({
    '@context': SCHEMA_CONTEXT,
    '@type': 'ItemList',
    name: 'Posts',
    itemListElement: posts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: post.title,
      url: `${getDomain(websiteDomain)}/${post.url}`,
      image: post.imageUrl,
    })),
  });
  return res;
}
function buildBreadcrumbsSchema(breadcrumbs?: BreadcrumbsType, id?: string, websiteDomain?: string): Schema {
  const breadcrumbsList = [breadcrumbs?.first, breadcrumbs?.second, breadcrumbs?.third];
  const schema: Schema = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbsList.filter(Boolean).map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb?.text?.value,

      ...(breadcrumb?.url ? { item: buildUrl(breadcrumb.url, websiteDomain) } : {}),
    })),
  };

  if (id) {
    schema['@id'] = id;
  }

  return schema;
}

//
// ---- UTILITIES ----
//
function createBlogBreadcrumbs(page: BlogPostPageType, websiteDomain: string): BreadcrumbsType {
  const breadcrumbs: BreadcrumbsType = {
    _type: 'breadcrumbs',
    first: {
      text: { value: 'Home' },
      url: `${getBlogUrl(websiteDomain)}/`,
    },
    second: undefined,
    third: undefined,
  };

  // Add category breadcrumb if exists
  if (page.categories && page.categories.length > 0) {
    breadcrumbs.second = {
      text: { value: page.categories[0].title?.value || '' },
      url: `${getBlogUrl(websiteDomain)}/category/${page.categories[0].slug}/`,
    };

    // Current page as third breadcrumb
    breadcrumbs.third = {
      text: { value: page.seo?.title?.value || '' },
      url: undefined, // Current page doesn't need URL
    };
  } else {
    // If no category, current page is second breadcrumb
    breadcrumbs.second = {
      text: { value: page.seo?.title?.value || '' },
      url: undefined, // Current page doesn't need URL
    };
  }

  return breadcrumbs;
}

function getLanguage(lang: string) {
  if (LANGUAGES.find((x) => x.id === lang)?.id === 'en') {
    return 'en-US';
  }
  if (LANGUAGES.find((x) => x.id === lang)?.id === 'de') {
    return 'de-DE';
  }
  if (LANGUAGES.find((x) => x.id === lang)?.id === 'es') {
    return 'es-ES';
  }
  if (LANGUAGES.find((x) => x.id === lang)?.id === 'nl') {
    return 'nl-NL';
  }
  return lang;
}

function addToSchemaHeroBreadcrumbs(
  schemas: Schema[],
  module: HeroType | HeroOverlapType,
  websiteDomain: string,
): Schema | undefined {
  let breadcrumbs: BreadcrumbsType | undefined;
  if (module._type === 'hero') {
    breadcrumbs = (module as HeroType).unicase?.breadcrumbs;
  }
  if (module._type === 'heroOverlap') {
    breadcrumbs = (module as HeroOverlapType).hero?.unicase?.breadcrumbs;
  }
  if (breadcrumbs?.first?.text?.value || breadcrumbs?.second?.text?.value || breadcrumbs?.third?.text?.value) {
    schemas.push(buildBreadcrumbsSchema(breadcrumbs, undefined, websiteDomain));
  }
  return undefined;
}

// Helper function to count words in Portable Text
function countWordsInPortableText(portableText?: FullPortableTextProjection['value']): number {
  if (!portableText || !Array.isArray(portableText)) {
    return 0;
  }

  const text = portableText
    .map((block) => {
      if (block?._type !== 'block' || !portableText?.length) {
        return;
      }
      if (block.children && Array.isArray(block.children)) {
        return block.children.map((child) => child.text || '').join(' ');
      }
      return '';
    })
    .join(' ')
    .trim();

  if (!text) {
    return 0;
  }

  // Split by whitespace and filter out empty strings
  const words = text.split(/\s+/).filter((word) => word.length > 0);
  return words.length;
}

// Helper function to convert plain text to rich text format
function convertTextToRichText(text: string, key: string) {
  return [
    {
      _type: 'block',
      _key: `block_${key}`,
      style: 'normal',
      children: [
        {
          _type: 'span',
          _key: `span_${key}`,
          text: text,
          marks: [],
        },
      ],
      markDefs: [],
    },
  ];
}

// FAQ data for specific blog posts
const FAQ_DATA = {
  'facebook-cover-photo-size': [
    {
      question: 'What size is the Facebook cover photo without cropping?',
      answer:
        'Use an image that precisely fits the recommended dimensions to ensure your Facebook cover photo is displayed without cropping. As of 2024, the ideal size for a Facebook cover photo is 820 pixels wide by 360 pixels tall. By using an image with these exact dimensions, you can avoid cropping issues. This size ensures that your cover photo will display correctly on both desktop and mobile devices without any portions being cut off.',
    },
    {
      question: "What's a good cover photo for Facebook?",
      answer:
        "Choosing the right cover photo for your Facebook profile or page depends on its purpose and the message you want to convey. Here are some helpful tips and ideas to assist you in selecting an impactful cover photo. 1. Branding: Include your brand's logo, colors, and tagline. Make sure the cover photo reflects your brand identity. 2. Products or Services: Showcase your main products or services with high-quality images demonstrating their use. 3. Promotions and Announcements: Utilize your cover photo to advertise special offers, new products, or upcoming events. Keep it updated regularly to keep your audience in the loop. 4. Customer Testimonials: Display positive quotes from satisfied customers and their photos (with permission).",
    },
  ],
  'advertisement-design-tips': [
    {
      question: 'What is an ad creative?',
      answer:
        "An ad creative is a crucial part of an advertising campaign. It consists of the visual and textual elements that form an advertisement, including images, videos, headlines, copy, call-to-action (CTA) buttons, and other content used to convey the ad's message to the target audience. The ad creative aims to capture attention, engage the audience, and prompt them to take specific action, such as clicking on a link, making a purchase, or signing up for a service. Compelling ad creatives are customized to the preferences and behaviors of the target audience and optimized for the platform where they will be displayed, whether social media, search engines, display networks, or other advertising channels. The goal is to create a compelling and persuasive advertisement that stands out and achieves the desired marketing objectives.",
    },
    {
      question: 'What should an ad contain?',
      answer:
        'A strong advertisement needs a clear and compelling headline that grabs attention and emphasizes the main benefit of the product or service. High-quality visual elements, such as images or videos, should complement the headline and enhance the ad\'s appeal. The body copy should provide detailed, concise information that focuses on the audience\'s needs, explaining the benefits and features in a relatable way. A clear and direct call to action (CTA) should prompt the audience to take the next step, such as "Buy Now" or "Learn More." Branding elements, including the logo, color scheme, and tagline, should be consistent with the overall brand identity to reinforce recognition. In addition, the ad should convey a strong value proposition that sets the product apart from competitors and builds an emotional connection through storytelling or relatable scenarios. Trust signals, such as customer testimonials or endorsements, should be included to build credibility. Finally, contact information should be easy to find, and any necessary legal and ethical disclaimers should be clearly stated to ensure compliance and trust.',
    },
  ],
  'ad-copy-tips-and-examples': [
    {
      question: 'What is ad copy?',
      answer:
        "Ad copy, also known as advertising copy, is the text used in advertisements to convince potential customers to take action, such as making a purchase, subscribing to a service, or visiting a website. It is a crucial element in marketing used across various media channels and advertising campaigns to grab the audience's attention, highlight the unique selling points of a product or service, evoke emotions, and ultimately inspire the desired action. Skilled copywriters create ad copy that resonates with the target audience, addressing their needs and pain points while showcasing how the offering can meet their requirements. In digital marketing, ad copy is essential for online advertising platforms like Google Ads and Facebook Ads, where optimization techniques such as A/B testing are used to refine copy and maximize its effectiveness in driving conversions and achieving advertising objectives.",
    },
    {
      question: 'What makes a good ad copy?',
      answer:
        "A well-written ad copy should be concise and easy to understand so the target audience can receive the message. It should be tailored to the needs and preferences of the audience while highlighting the unique selling proposition (USP) of the product or service to differentiate it from competitors. By appealing to emotions and showcasing the benefits instead of just features, the ad establishes a strong connection with the audience and motivates them to take action through a clear and compelling call to action (CTA). Creativity and originality are also crucial to make the ad stand out, while consistency with the brand voice and image strengthens brand recognition and trust. In digital advertising, using relevant keywords and phrases can increase the ad's relevance, and ongoing testing and optimization are necessary for continuous improvement in achieving advertising goals.",
    },
    {
      question: 'Who needs a copywriter?',
      answer:
        'A copywriter can be hired by various individuals and organizations such as businesses, marketing agencies, e-commerce websites, non-profit organizations, authors, startups, educational institutions, government agencies, and event planners. Their services help create engaging content for marketing materials, websites, advertisements, product descriptions, fundraising appeals, social media posts, and promotional campaigns, including great Facebook ads. Copywriters are experts in crafting messaging that resonates with target audiences, and their services extend to various platforms. They can help with multiple tasks, such as optimizing website content for search engines and developing persuasive storytelling content to drive engagement and conversions. Their skills are applicable across diverse industries and sectors.',
    },
  ],
} as const;

// Helper function to create FAQ question object
const createFAQQuestion = (question: string, answer: string, key: string) =>
  ({
    question: { value: question },
    answer: { value: answer },
    answerRich: convertTextToRichText(answer, key),
  }) as QuestionProjection;

function addFAQEdgeCases(schemas: Schema[], page: BlogPostPageType) {
  const faqData = FAQ_DATA[page.slug as keyof typeof FAQ_DATA];
  if (faqData) {
    const questions = faqData.map((faq, index) => createFAQQuestion(faq.question, faq.answer, `${page.slug}_${index}`));

    schemas.push(
      buildFAQSchema({
        questions: questions,
      }),
    );
  }
}

function formatDate(date?: Date | string | null): string {
  if (!date) {
    return '';
  }
  return new Date(date).toISOString();
}

function buildUrl(url: string, websiteDomain?: string): string {
  if (url.startsWith('http')) {
    return url;
  }
  if (url.startsWith('javascript:')) {
    return url;
  }
  if (url.startsWith('/')) {
    return websiteDomain ? `${getDomain(websiteDomain)}${url}` : url;
  }
  return websiteDomain ? `${getDomain(websiteDomain)}/${url}` : `/${url}`;
}
